import React from 'react';
import { useNavigate } from 'react-router-dom';
import './ConversationArea.css';
import MessageBubble from '../common/MessageBubble';
import { useConversation } from '../../state/ConversationContext';
import { useAuth } from '../../state/AuthContext';
import QuickActions from '../chat/QuickActions';
import ActivationExperience from '../Activation/ActivationExperience';
import { userService } from '../../services/userService';
import OverageBanner from '../Billing/OverageBanner';
import { APP_ROUTES } from '../../routes/routingContract';
import StateBlock from '../common/StateBlock';

export default function ConversationArea() {
  const navigate = useNavigate();
  const {
    messages,
    currentThreadId,
    threads,
    overageWarning,
    dismissOverageWarning,
    upgradePrompt,
    threadLoadError,
    loadThread,
    sendText,
  } = useConversation();
  const { session, billingStatus } = useAuth();
  const [userInfo, setUserInfo] = React.useState(null);
  const [isLoadingUser, setIsLoadingUser] = React.useState(true);
  const bottomRef = React.useRef(null);
  const scrollContainerRef = React.useRef(null);
  const scrollContainerEl = React.useRef(null); // the actual .content__scroll DOM node
  const [isAtBottom, setIsAtBottom] = React.useState(true);
  const userHasScrolled = React.useRef(false);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!session?.userId) {
          setIsLoadingUser(false);
          return;
        }
        const data = await userService.getUser(session.userId);
        if (!cancelled) {
          setUserInfo(data);
          setIsLoadingUser(false);
        }
      } catch (e) {
        if (!cancelled) {
          setUserInfo(null);
          setIsLoadingUser(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session?.userId]);


  const firstName =
    (userInfo?.first_name && userInfo.first_name.trim()) ||
    'there';

  const showThreadError = Boolean(currentThreadId && threadLoadError);
  const showActivationExperience = !showThreadError && !currentThreadId && messages.length === 0 && threads.length === 0;
  const showQuickActions = !showThreadError && !currentThreadId && messages.length === 0 && !showActivationExperience;
  const trialMessageLimitReached = billingStatus?.billing_status === 'trialing' && billingStatus?.message_limit_reached;
  const trialMessagesRemaining = billingStatus?.billing_status === 'trialing'
    ? billingStatus?.messages_remaining
    : null;
  const showTrialNotice = billingStatus?.billing_status === 'trialing'
    && (trialMessageLimitReached || upgradePrompt || (Number.isFinite(trialMessagesRemaining) && trialMessagesRemaining <= 2));
  const trialNoticeText = trialMessageLimitReached || upgradePrompt
    ? 'You hit your free message limit. Upgrade to keep chatting.'
    : trialMessagesRemaining === 1
    ? 'This is your last free message.'
    : `You have ${trialMessagesRemaining} free messages left.`;

  const goToUpgrade = React.useCallback(() => {
    navigate(APP_ROUTES.upgrade, {
      state: {
        backgroundPath: currentThreadId ? `/app/chat/${encodeURIComponent(currentThreadId)}` : APP_ROUTES.chat,
        upgradeMessage: trialMessageLimitReached || upgradePrompt
          ? 'You hit your free message limit. Upgrade to keep chatting.'
          : 'You can upgrade now or continue with your free trial.',
      },
    });
  }, [currentThreadId, navigate, trialMessageLimitReached, upgradePrompt]);

  // Detect scroll position - find parent scroll container
  React.useEffect(() => {
    // Find the parent .content__scroll element
    const findScrollContainer = (element) => {
      let parent = element?.parentElement;
      while (parent) {
        if (parent.classList.contains('content__scroll')) {
          return parent;
        }
        parent = parent.parentElement;
      }
      return null;
    };

    const container = findScrollContainer(scrollContainerRef.current);
    if (!container) return;
    scrollContainerEl.current = container;

    const checkIfAtBottom = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const atBottom = Math.abs(scrollHeight - scrollTop - clientHeight) < 10; // 10px threshold
      setIsAtBottom(atBottom);

      // If user manually scrolled, mark it
      if (!atBottom && scrollTop > 0) {
        userHasScrolled.current = true;
      }
    };

    container.addEventListener('scroll', checkIfAtBottom);
    checkIfAtBottom(); // Initial check

    return () => container.removeEventListener('scroll', checkIfAtBottom);
  }, []);

  // Smart auto-scroll: only scroll if user is at bottom or hasn't manually scrolled.
  // Use direct scrollTop assignment — no animation, no queuing, no jitter during streaming.
  React.useEffect(() => {
    if (isAtBottom || !userHasScrolled.current) {
      const el = scrollContainerEl.current;
      if (el) el.scrollTop = el.scrollHeight;
      userHasScrolled.current = false;
    }
  }, [messages, isAtBottom]);
  return (
    <section className="conversation" role="log" aria-live="polite" aria-relevant="additions text">
      {showTrialNotice && (
        <div className={`trial-top-banner${trialMessageLimitReached || upgradePrompt ? ' limit' : ''}`} role="status">
          <span>{trialNoticeText}</span>
          <button type="button" onClick={goToUpgrade}>
            Upgrade
          </button>
        </div>
      )}
      <div ref={scrollContainerRef} className={`messages ${showQuickActions || showActivationExperience ? 'quick-actions-only' : ''}`} role="list">
        {overageWarning && (
          <OverageBanner
            used={overageWarning.used}
            quota={overageWarning.quota}
            overageCostCents={overageWarning.overageCostCents}
            periodEnd={overageWarning.periodEnd}
            nextTier={overageWarning.nextTier}
            onDismiss={dismissOverageWarning}
          />
        )}
        {showThreadError && (
          <StateBlock
            state="error"
            title={threadLoadError.title}
            message={threadLoadError.message}
            actionLabel={threadLoadError.actionLabel || 'Retry'}
            onAction={() => loadThread(currentThreadId)}
          />
        )}
        {showActivationExperience && (
          <ActivationExperience />
        )}
        {showQuickActions && !isLoadingUser && (
          <>
            <div className="quick-actions-hero">
              {/* Ambient glow — decorative only */}
              <div className="ambient-glow" aria-hidden="true"></div>

              <h2>Hello, <span className="greeting-name">{firstName}</span></h2>
              <p>What would you like to know about your money?</p>
            </div>
            <QuickActions />
          </>
        )}
        {messages.map((m) => (
          <div key={m.id} role="listitem" className={`message-block ${m.role}`}>
            <MessageBubble
              message={m}
              onRetry={m.retryText ? () => sendText(m.retryText) : undefined}
            />
          </div>
        ))}
        <div ref={bottomRef} />
      </div>
    </section>
  );
}
